import { is } from 'immutable';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useQuery } from '@burnsred/entity-duck-query';

import { axiosInstance } from '@/api';
import type { WhoamiEntityFields, WhoamiEntityRecord } from '@/entities/Whoami';
import WhoamiEntity from '@/entities/Whoami';
import { settings } from '@/settings';
import { createLogger } from '@/utils';

const log = createLogger('useWhoAmIPrivate');

/**
 * Fetches User via the whoami endpoint
 *
 * Should only be called by the WhoAmI context provider!
 * You probably want useWhoAmI from there.
 */
export const useWhoAmIPrivate = () => {
  const formProps = useQuery<WhoamiEntityRecord>({
    action: WhoamiEntity.duck.actions.whoAmI(),
  });

  /**
   * Note: `value` reads from `*_dirty` Redux state (meant for tracking form edits),
   * while `valueInitial` reads from the clean state (server response).
   *
   * Since whoAmI is a fetch-only action with no form editing, the dirty slot is
   * never populated, so value is always undefined.
   */
  const user = formProps.valueInitial;

  /** `true` while the request is in flight */
  const authenticating = useSelector<unknown, boolean>(
    (state) => WhoamiEntity.duck.selectors.authenticating(state),
    is,
  );

  /**
   * Track whether the whoAmI request has been initiated.
   *
   * We can't rely solely on Redux flags because a 401 response resets the
   * auth namespace to its initial state (`whoAmIed: false`), making it
   * indistinguishable from "never checked". This state latches to `true`
   * once `authenticating` becomes `true` (i.e. the request was dispatched)
   * and stays there for the lifetime of the component.
   */
  const [hasStarted, setHasStarted] = useState(false);
  if (authenticating && !hasStarted) setHasStarted(true);

  const dispatch = useDispatch();

  const logIn = useCallback(
    async (username: string, password: string) => {
      const response = await axiosInstance.post<WhoamiEntityFields>(
        settings.AUTH_URL,
        {
          username,
          password,
        },
      );
      await dispatch(WhoamiEntity.duck.actions.whoAmI_resolved(response.data))
        .promise;
      return response;
    },
    [dispatch],
  );

  const endSession = useCallback(() => {
    dispatch(
      WhoamiEntity.duck.actions.whoAmI_rejected({
        response: { status: 401 },
      }),
    );
  }, [dispatch]);

  const logOut = useCallback(async () => {
    await axiosInstance.delete(settings.AUTH_URL);
    endSession();
  }, [endSession]);

  const isLoading = !hasStarted || authenticating;
  log('%o', { isLoading, user, hasStarted, authenticating });

  return {
    endSession,
    formProps,
    isLoading,
    logIn,
    logOut,
    user,
  };
};

export type UseWhoAmIReturn = ReturnType<typeof useWhoAmIPrivate>;
